/**
 * O DIAGNÓSTICO DE ORIGEM — o que faz esta tela valer alguma coisa.
 *
 * O desfecho mais perigoso deste produto não é um erro: é a instalação errada que PARECE
 * funcionar. Com o nosso link num `href` estático, o redirect funciona, o grupo é alocado, a
 * pessoa entra — e a ligação com a campanha morre sem um único erro em lugar nenhum. Do lado
 * de fora isso se lê como "a campanha não converte", e o cliente desliga o anúncio certo.
 *
 * Este painel é o que transforma o estado `NO_ORIGIN` (ADR-0013) em frase acionável. Por isso
 * ele tem três partes e não uma: **o que está acontecendo** (com os números do servidor),
 * **por que provavelmente está acontecendo** (as causas em ordem de frequência) e **o que
 * fazer** (o trecho, que fica na aba de instalação, logo abaixo).
 *
 * ## O que este painel NÃO faz
 *
 *  - **Não calcula porcentagem.** Os dois números vêm contados do banco (`clicksTotal`,
 *    `clicksNoOrigin`) e aparecem como vieram. Um "87%" montado aqui seria um segundo caminho
 *    para um número que a Fase 8 vai calcular no servidor — e dois caminhos para o mesmo
 *    número é como um deles fica errado sem ninguém notar (invariante 3).
 *  - **Não confunde `0` com "sem dados".** Link recém-criado não está com "0% de origem": ele
 *    não tem medição nenhuma, e o painel diz exatamente isso (invariante 8).
 *  - **Não fala em período.** O contrato não expõe filtro de data; estes números são a vida
 *    inteira do link, e o painel escreve isso em vez de deixar parecer "hoje".
 */
import { Badge } from '@mgdlead/ui';
import { AlertTriangle, CheckCircle2, CircleDashed } from 'lucide-react';

import {
  CAUSAS_SEM_ORIGEM,
  DIAGNOSIS,
  QUANDO_E_SEM_ORIGEM,
  diagnoseOrigin,
  precisaDeConserto,
} from '../../../features/redirector/redirector-ui';
import type { DiagnosisInput } from '../../../features/redirector/redirector-ui';

export interface OriginDiagnosisPanelProps {
  redirector: DiagnosisInput;
  /** `compact` mostra só a frase — é a versão que cabe numa linha da lista. */
  compact?: boolean;
}

const ICONE = {
  SEM_CLIQUES: CircleDashed,
  TUDO_COM_ORIGEM: CheckCircle2,
  PARTE_SEM_ORIGEM: AlertTriangle,
  NADA_COM_ORIGEM: AlertTriangle,
} as const;

const BORDA = {
  SEM_CLIQUES: 'border-border',
  TUDO_COM_ORIGEM: 'border-success/40',
  PARTE_SEM_ORIGEM: 'border-warning/40',
  NADA_COM_ORIGEM: 'border-danger/40',
} as const;

const COR = {
  SEM_CLIQUES: 'text-muted-foreground',
  TUDO_COM_ORIGEM: 'text-success',
  PARTE_SEM_ORIGEM: 'text-warning',
  NADA_COM_ORIGEM: 'text-danger',
} as const;

export function OriginDiagnosisPanel({ redirector, compact = false }: OriginDiagnosisPanelProps) {
  const diagnosis = diagnoseOrigin(redirector);
  const spec = DIAGNOSIS[diagnosis.kind];
  const Icon = ICONE[diagnosis.kind];
  const conserto = precisaDeConserto(diagnosis);

  if (compact) {
    return (
      <span className="flex items-start gap-1.5">
        <Icon aria-hidden="true" className={`mt-0.5 size-3.5 shrink-0 ${COR[diagnosis.kind]}`} />
        <span className="text-2xs text-muted-foreground">{spec.summary(diagnosis)}</span>
      </span>
    );
  }

  return (
    <section
      className={`flex flex-col gap-2 rounded-lg border bg-surface p-3 ${BORDA[diagnosis.kind]}`}
      aria-label="Diagnóstico de origem dos cliques"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Icon aria-hidden="true" className={`size-4 shrink-0 ${COR[diagnosis.kind]}`} />
        <h3 className="text-md font-medium">Origem dos cliques</h3>
        <Badge tone={spec.tone} dot>
          {spec.label}
        </Badge>
      </div>

      <p className="text-sm">{spec.summary(diagnosis)}</p>

      {/*
        A contagem é da vida inteira do link. Dizer isso é o que impede alguém de olhar o
        número depois de consertar a página e concluir que o conserto não funcionou — os
        cliques ruins de ontem continuam na conta.
      */}
      {diagnosis.kind === 'SEM_CLIQUES' ? null : (
        <p className="text-2xs text-muted-foreground">
          A contagem é de todos os cliques deste link desde que ele foi criado, não de um período.
          Depois de consertar a página, o que muda são os cliques NOVOS.
        </p>
      )}

      {conserto ? (
        <>
          <p className="text-2xs text-muted-foreground">{QUANDO_E_SEM_ORIGEM}</p>

          <div className="flex flex-col gap-2 border-t border-border pt-2">
            <p className="text-xs font-medium">O que costuma estar errado, nesta ordem:</p>
            <ol className="flex list-decimal flex-col gap-1.5 pl-4">
              {CAUSAS_SEM_ORIGEM.map((causa) => (
                <li key={causa.titulo} className="text-2xs text-muted-foreground">
                  <span className="font-medium text-foreground">{causa.titulo}.</span> {causa.texto}
                </li>
              ))}
            </ol>
          </div>
        </>
      ) : null}
    </section>
  );
}
